import "leaflet/dist/leaflet.css";

import { divIcon } from "leaflet";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  KeyRound,
  Loader2,
  MapPin,
  PackageCheck,
  Route,
  Truck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import ModalDropiIntegration from "../../../../components/integrations/Modals/ModalDropiIntegration";
import { useAuth } from "../../../../context/AuthContext";
import { postDropiIntegration } from "../../../../services/integrations/integrationsService";
import { dictionary, type Language } from "../../../../utils/dictionary";
import { ChatLayoutWrapper, type ChatContext } from "../ai-chat";
import type { Period } from "../funnel-detail/types";
import { ChannelTable, type ChannelRow } from "./ChannelBreakdown";
import { CompanyLogisticsSection } from "./company/CompanyLogisticsSection";
import { downloadCsv, orderCsvRows } from "./financeCsv";
import {
  financeService,
  getFinanceLogisticsOverview,
  type FinanceLogisticsMapBubble,
  type FinanceLogisticsMapOrder,
  type FinanceLogisticsOrder,
  type FinanceLogisticsOverview,
  type FinanceLogisticsStatusBucket,
} from "../../services/financeService";

type LogisticsTabProps = {
  period: Period;
};

const statusMeta: Record<string, { color: string; dot: string; bg: string }> = {
  pending: { color: "text-slate-700", dot: "#64748b", bg: "bg-slate-100" },
  guide_generated: { color: "text-blue-700", dot: "#2563eb", bg: "bg-blue-50" },
  in_transit: { color: "text-amber-700", dot: "#d97706", bg: "bg-amber-50" },
  delivered: { color: "text-emerald-700", dot: "#059669", bg: "bg-emerald-50" },
  returned: { color: "text-rose-700", dot: "#e11d48", bg: "bg-rose-50" },
  incident: { color: "text-orange-700", dot: "#ea580c", bg: "bg-orange-50" },
  error: { color: "text-red-700", dot: "#dc2626", bg: "bg-red-50" },
};

const formatCurrency = (value: number, currency = "COP") =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(value || 0);

const getDropiIntegrationErrorMessage = (error: unknown, language: Language) => {
  const data = (error as { response?: { data?: { trigger?: string; message?: string } } })?.response?.data;
  const trigger = data?.trigger;
  if (trigger && dictionary[trigger]?.[language]) return dictionary[trigger][language];
  return data?.message || dictionary["integration-error"]?.[language] || "No pudimos conectar Dropi";
};

const createBubbleIcon = (count: number, status: string) => {
  const color = status === "all" ? "#2b5ce6" : statusMeta[status]?.dot ?? "#2563eb";
  const size = Math.max(26, Math.min(76, 22 + Math.sqrt(count) * 9));
  return divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:999px;background:${color};border:3px solid white;box-shadow:0 12px 28px rgba(15,23,42,.24);display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:12px">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

export const LogisticsTab = ({ period }: LogisticsTabProps) => {
  const { language } = useAuth();
  const [overview, setOverview] = useState<FinanceLogisticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpenDropi, setModalOpenDropi] = useState(false);
  const [token, setToken] = useState("");
  const [activeMapStatus, setActiveMapStatus] = useState("all");

  const loadOverview = useCallback(async () => {
    setLoading(true);
    const data = await getFinanceLogisticsOverview(period);
    setOverview(data);
    setLoading(false);
  }, [period]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getFinanceLogisticsOverview(period).then((data) => {
      if (!mounted) return;
      setOverview(data);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [period]);

  const handleConnectDropi = async (dropiToken: string) => {
    const cleanToken = dropiToken.trim();
    if (!cleanToken) return;

    try {
      await postDropiIntegration({ integration_token: cleanToken, is_active: true });
      setModalOpenDropi(false);
      setToken("");
      toast.success("Dropi conectado");
      await loadOverview();
    } catch (error) {
      toast.error(getDropiIntegrationErrorMessage(error, language), { duration: 6000 });
    }
  };

  const handleSync = useCallback(async () => {
    try {
      const result = await financeService.startDropiBackfill(period);
      if (!result.ok) {
        throw new Error(result.message || "No pudimos actualizar Dropi");
      }
      await loadOverview();
      toast.success(result.message || "Logística actualizada");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos actualizar Dropi");
    }
  }, [loadOverview, period]);

  useEffect(() => {
    const handleExternalSync = () => {
      void handleSync();
    };
    window.addEventListener("finance:sync-logistics", handleExternalSync);
    return () => {
      window.removeEventListener("finance:sync-logistics", handleExternalSync);
    };
  }, [handleSync]);

  const chatContext = useMemo<ChatContext>(() => {
    if (!overview) {
      return {
        surface: "logistics",
        period: {
          id: period.id,
          label: period.label,
          from: period.from.toISOString().slice(0, 10),
          to: period.to.toISOString().slice(0, 10),
          days: period.days,
        },
        summary: "Fluxi Finance Logística cargando.",
      };
    }

    return {
      surface: "logistics",
      period: {
        id: period.id,
        label: period.label,
        from: period.from.toISOString().slice(0, 10),
        to: period.to.toISOString().slice(0, 10),
        days: period.days,
      },
      summary: [
        "Fluxi Finance Logística",
        `Periodo: ${period.label}`,
        `Dropi conectado: ${overview.dropi_connected ? "sí" : "no"}`,
        `Pedidos: ${overview.summary.total_orders}`,
        `Entregados: ${overview.summary.delivered}`,
        `Novedades: ${overview.summary.incidents}`,
        `Costos variables: ${formatCurrency(overview.summary.variable_cost, overview.summary.currency)}`,
        `Producto: ${formatCurrency(overview.summary.product_cost, overview.summary.currency)}`,
        `Flete/envío: ${formatCurrency(overview.summary.shipping_cost, overview.summary.currency)}`,
      ].join("\n"),
    };
  }, [overview, period]);

  if (loading || !overview) {
    return (
      <div className="flex min-h-[360px] items-center justify-center text-sm font-medium text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Cargando logística...
      </div>
    );
  }

  return (
    <ChatLayoutWrapper context={chatContext}>
      <div className="space-y-6">
        {!overview.dropi_connected ? (
          <DropiRequiredState onConnect={() => setModalOpenDropi(true)} />
        ) : (
          <>
            <div id="finance-logistics-summary-target">
              <SummaryCards overview={overview} />
            </div>

            <CompanyLogisticsSection period={period} />

            <div id="finance-logistics-map-status-target" className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,.8fr)]">
              <LogisticsMap
                orders={overview.map_orders}
                mapBubbles={overview.map_bubbles ?? []}
                activeStatus={activeMapStatus}
              />
              <StatusBreakdown
                buckets={overview.status_breakdown}
                currency={overview.summary.currency}
                activeStatus={activeMapStatus}
                onStatusChange={setActiveMapStatus}
              />
            </div>

            <div id="finance-logistics-orders-target">
              <OrdersTable orders={overview.orders} />
            </div>

            <LogisticsChannelsSection overview={overview} />
          </>
        )}

        {modalOpenDropi ? (
          <ModalDropiIntegration
            setModalOpenDropi={setModalOpenDropi}
            handlePostDropiIntegration={handleConnectDropi}
            token={token}
            setToken={setToken}
            language={language}
          />
        ) : null}
      </div>
    </ChatLayoutWrapper>
  );
};

function DropiRequiredState({ onConnect }: { onConnect: () => void }) {
  return (
    <div id="finance-logistics-connect-target" className="rounded-lg border border-orange-100 bg-white px-6 py-8 shadow-sm">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
            <KeyRound className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Conecta Dropi para ver logística</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
              La pestaña usa órdenes, guías, transportadora y novedades de Dropi para calcular entregas,
              devoluciones y pedidos en riesgo.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onConnect}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#F38244] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#e87537]"
        >
          <Truck className="h-4 w-4" />
          Conectar Dropi
        </button>
      </div>
    </div>
  );
}

function SummaryCards({ overview }: { overview: FinanceLogisticsOverview }) {
  const { summary } = overview;
  const logisticsCost =
    Number(summary.shipping_cost ?? 0) +
    Number(summary.fulfillment_cost ?? 0) +
    Number(summary.platform_commission ?? 0) +
    Number(summary.return_cost ?? 0);
  const cards = [
    {
      label: "Pedidos Dropi",
      value: summary.dropi_orders.toString(),
      detail: `${summary.with_guide} con guía`,
      icon: Truck,
    },
    {
      label: "Entregas",
      value: `${summary.delivery_rate.toFixed(1)}%`,
      detail: `${summary.delivered} entregados`,
      icon: CheckCircle2,
    },
    {
      label: "Novedades",
      value: summary.incidents.toString(),
      detail: `${summary.returned} devueltos`,
      icon: AlertTriangle,
    },
    {
      label: "Costos variables",
      value: formatCurrency(summary.variable_cost, summary.currency),
      detail: `Producto ${formatCurrency(summary.product_cost, summary.currency)} · Logística ${formatCurrency(logisticsCost, summary.currency)}`,
      icon: Route,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.label} className="rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase text-gray-500">{card.label}</p>
              <Icon className="h-4 w-4 text-gray-400" />
            </div>
            <p className="mt-2 text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="mt-1 text-xs text-gray-500">{card.detail}</p>
          </div>
        );
      })}
    </div>
  );
}

function LogisticsMap({
  orders,
  mapBubbles,
  activeStatus,
}: {
  orders: FinanceLogisticsMapOrder[];
  mapBubbles: FinanceLogisticsMapBubble[];
  activeStatus: string;
}) {
  const bubbles = useMemo(() => {
    if (mapBubbles.length > 0) {
      const scoped = activeStatus === "all"
        ? mapBubbles
        : mapBubbles.filter((bubble) => bubble.status === activeStatus);
      const grouped = new Map<string, {
        key: string;
        label: string;
        latitude: number;
        longitude: number;
        count: number;
        value: number;
      }>();

      scoped.forEach((bubble) => {
        const key = bubble.label.toLowerCase();
        const current = grouped.get(key) ?? {
          key,
          label: bubble.label,
          latitude: bubble.latitude,
          longitude: bubble.longitude,
          count: 0,
          value: 0,
        };
        current.count += Number(bubble.count ?? 0);
        current.value += Number(bubble.value ?? 0);
        grouped.set(key, current);
      });

      return [...grouped.values()].sort((a, b) => b.count - a.count);
    }

    const filteredOrders = activeStatus === "all" ? orders : orders.filter((order) => order.status === activeStatus);
    const grouped = new Map<string, {
      key: string;
      label: string;
      latitude: number;
      longitude: number;
      count: number;
      value: number;
      statuses: Map<string, number>;
    }>();

    filteredOrders.forEach((order) => {
      const label = order.department || order.city || "Sin ubicación";
      const key = label.toLowerCase();
      const current = grouped.get(key) ?? {
        key,
        label,
        latitude: order.latitude,
        longitude: order.longitude,
        count: 0,
        value: 0,
        statuses: new Map<string, number>(),
      };
      current.count += 1;
      current.value += Number(order.value ?? 0);
      current.statuses.set(order.status, (current.statuses.get(order.status) ?? 0) + 1);
      grouped.set(key, current);
    });

    return [...grouped.values()].sort((a, b) => b.count - a.count);
  }, [activeStatus, mapBubbles, orders]);

  if (bubbles.length === 0) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <MapPin className="h-4 w-4 text-blue-600" />
          Mapa de pedidos
        </div>
        <div className="mt-4 flex h-[520px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500">
          Sin pedidos con ubicación en este periodo
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <MapPin className="h-4 w-4 text-blue-600" />
        Mapa de pedidos
      </div>
      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
        <MapContainer
          center={[4.5709, -74.2973]}
          zoom={5}
          minZoom={4}
          maxZoom={12}
          scrollWheelZoom={false}
          className="h-[520px] w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {bubbles.map((bubble) => (
            <Marker
              key={bubble.key}
              position={[bubble.latitude, bubble.longitude]}
              icon={createBubbleIcon(bubble.count, activeStatus)}
            >
              <Popup>
                <div className="min-w-[180px] text-sm">
                  <p className="font-semibold text-gray-900">{bubble.label}</p>
                  <p className="mt-1 text-gray-600">{bubble.count} pedidos</p>
                  <p className="mt-1 font-medium text-gray-800">{formatCurrency(bubble.value)}</p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </section>
  );
}

function StatusBreakdown({
  buckets,
  currency,
  activeStatus,
  onStatusChange,
}: {
  buckets: FinanceLogisticsStatusBucket[];
  currency: string;
  activeStatus: string;
  onStatusChange: (status: string) => void;
}) {
  const total = buckets.reduce((acc, bucket) => acc + bucket.count, 0);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <PackageCheck className="h-4 w-4 text-emerald-600" />
        Estados
      </div>
      <div className="mt-4 space-y-4">
        <button
          type="button"
          onClick={() => onStatusChange("all")}
          className={`w-full rounded-lg border px-3 py-2 text-left transition ${
            activeStatus === "all" ? "border-[#2b5ce6] bg-blue-50" : "border-transparent hover:bg-gray-50"
          }`}
        >
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-gray-900">Todos</span>
            <span className="text-gray-500">{formatNumber(total)}</span>
          </div>
        </button>
        {buckets.map((bucket) => {
          const pct = total > 0 ? (bucket.count / total) * 100 : 0;
          const meta = statusMeta[bucket.id] ?? statusMeta.pending;
          return (
            <button
              key={bucket.id}
              type="button"
              onClick={() => onStatusChange(bucket.id)}
              className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                activeStatus === bucket.id ? "border-[#2b5ce6] bg-blue-50" : "border-transparent hover:bg-gray-50"
              }`}
            >
              <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                <span className={`font-medium ${meta.color}`}>{bucket.label}</span>
                <span className="text-gray-500">{bucket.count}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: meta.dot }} />
              </div>
              <p className="mt-1 text-xs text-gray-400">{formatCurrency(bucket.value, currency)}</p>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function OrdersTable({ orders }: { orders: FinanceLogisticsOrder[] }) {
  const pageSize = 10;
  const [productFilter, setProductFilter] = useState("");
  const [destinationFilter, setDestinationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const normalizedProduct = productFilter.trim().toLowerCase();
  const normalizedDestination = destinationFilter.trim().toLowerCase();
  const carriers = useMemo(
    () => [...new Set(orders.map((order) => order.carrier || "Dropi"))].sort(),
    [orders]
  );
  const statuses = useMemo(
    () => [...new Map(orders.map((order) => [order.status, order.status_label])).entries()],
    [orders]
  );
  const filteredOrders = useMemo(
    () =>
      orders
        .filter((order) => {
          const destination = `${order.city ?? ""} ${order.department ?? ""}`.toLowerCase();
          const product = (order.product_names ?? "").toLowerCase();
          if (normalizedProduct && !product.includes(normalizedProduct)) return false;
          if (normalizedDestination && !destination.includes(normalizedDestination)) return false;
          if (statusFilter !== "all" && order.status !== statusFilter) return false;
          if (carrierFilter !== "all" && (order.carrier || "Dropi") !== carrierFilter) return false;
          return true;
        }),
    [carrierFilter, normalizedDestination, normalizedProduct, orders, statusFilter]
  );
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const visibleOrders = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [currentPage, filteredOrders, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [carrierFilter, normalizedDestination, normalizedProduct, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Truck className="h-4 w-4 text-gray-500" />
            Pedidos
          </div>
          <button
            type="button"
            onClick={() => downloadCsv("logistica-pedidos.csv", orderCsvRows(orders))}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            <Download className="h-4 w-4 text-[#2b5ce6]" />
            Descargar todo
          </button>
        </div>
        <div className="grid gap-2 md:grid-cols-4">
          <input
            value={productFilter}
            onChange={(event) => setProductFilter(event.target.value)}
            placeholder="Producto"
            className="h-9 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#2b5ce6]"
          />
          <input
            value={destinationFilter}
            onChange={(event) => setDestinationFilter(event.target.value)}
            placeholder="Destino"
            className="h-9 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#2b5ce6]"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-9 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#2b5ce6]"
          >
            <option value="all">Todos los estados</option>
            {statuses.map(([status, label]) => (
              <option key={status} value={status}>{label}</option>
            ))}
          </select>
          <select
            value={carrierFilter}
            onChange={(event) => setCarrierFilter(event.target.value)}
            className="h-9 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#2b5ce6]"
          >
            <option value="all">Todas las transportadoras</option>
            {carriers.map((carrier) => (
              <option key={carrier} value={carrier}>{carrier}</option>
            ))}
          </select>
        </div>
        <span className="text-xs font-medium text-gray-400">
          Página {Math.min(currentPage, totalPages)} de {totalPages} · {filteredOrders.length} de {orders.length} pedidos filtrados
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Orden</th>
              <th className="px-5 py-3 font-semibold">Destino</th>
              <th className="px-5 py-3 font-semibold">Estado</th>
              <th className="px-5 py-3 font-semibold">Guía</th>
              <th className="px-5 py-3 font-semibold text-right">Venta</th>
              <th className="px-5 py-3 font-semibold text-right">Producto</th>
              <th className="px-5 py-3 font-semibold text-right">Flete</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleOrders.map((order) => (
              <tr key={order.order_id} className="align-top">
                <td className="px-5 py-4">
                  <p className="font-semibold text-gray-900">#{order.order_hash_id || order.order_id}</p>
                  <p className="mt-1 max-w-[220px] truncate text-xs text-gray-500">
                    {order.product_names || "Producto sin dato"}
                  </p>
                </td>
                <td className="px-5 py-4 text-gray-600">
                  <p>{order.city || "Sin ciudad"}</p>
                  <p className="mt-1 text-xs text-gray-400">{order.department || "Sin departamento"}</p>
                </td>
                <td className="px-5 py-4">
                  <StatusPill status={order.status} label={order.status_label} />
                  {order.last_error ? <p className="mt-2 max-w-[220px] text-xs text-red-500">{order.last_error}</p> : null}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  <p>{order.guide_number || "Sin guía"}</p>
                  <p className="mt-1 text-xs text-gray-400">{order.carrier || "Dropi"}</p>
                </td>
                <td className="px-5 py-4 text-right font-semibold text-gray-900">{formatCurrency(order.value, order.currency)}</td>
                <td className="px-5 py-4 text-right text-gray-700">{formatCurrency(Number(order.product_cost ?? 0), order.currency)}</td>
                <td className="px-5 py-4 text-right text-gray-700">{formatCurrency(Number(order.shipping_cost ?? 0), order.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {orders.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-500">Sin pedidos de Dropi en este periodo</div>
      ) : null}
      {filteredOrders.length > pageSize ? (
        <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="font-medium text-gray-500">
            Mostrando {(Math.min(currentPage, totalPages) - 1) * pageSize + 1}-{Math.min(Math.min(currentPage, totalPages) * pageSize, filteredOrders.length)} de {filteredOrders.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage <= 1}
              className="h-9 rounded-lg border border-gray-200 px-3 font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="min-w-[92px] text-center text-xs font-bold text-gray-500">
              {Math.min(currentPage, totalPages)} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage >= totalPages}
              className="h-9 rounded-lg border border-gray-200 px-3 font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function LogisticsChannelsSection({ overview }: { overview: FinanceLogisticsOverview }) {
  const summary = overview.summary;
  const dropiSyncedOrders = overview.orders.filter((order) => Boolean(order.funnel_id));
  const dropiUnsyncedOrders = overview.orders.filter((order) => !order.funnel_id);
  const totalValue =
    Number(summary.matched_value ?? 0) +
    Number(summary.unmatched_value ?? 0) ||
    overview.status_breakdown.reduce((sum, bucket) => sum + Number(bucket.value ?? 0), 0);
  const matchedValue = Number(summary.matched_value ?? 0);
  const unmatchedValue = Number(summary.unmatched_value ?? 0);
  const rows: ChannelRow[] = [
    {
      label: "Fluxi",
      detail: "Pedidos que sí logramos asociar a un embudo.",
      countLabel: "Pedidos",
      count: Number(summary.matched_orders ?? 0),
      valueLabel: "Valor",
      value: matchedValue,
      share: totalValue > 0 ? (matchedValue / totalValue) * 100 : 0,
      onDownload: () => downloadCsv("ventas-fluxi.csv", orderCsvRows(dropiSyncedOrders)),
    },
    {
      label: "Otros canales de venta",
      detail: "Pedidos del negocio que no pertenecen a embudos Fluxi.",
      countLabel: "Pedidos",
      count: Number(summary.unmatched_orders ?? 0),
      valueLabel: "Valor",
      value: unmatchedValue,
      share: totalValue > 0 ? (unmatchedValue / totalValue) * 100 : 0,
      onDownload: () => downloadCsv("ventas-otros-canales.csv", orderCsvRows(dropiUnsyncedOrders)),
    },
    {
      label: "Total Dropi",
      detail: "Pedidos capturados en el periodo.",
      countLabel: "Pedidos",
      count: Number(summary.total_orders ?? 0),
      valueLabel: "Valor",
      value: totalValue,
      share: 100,
      variant: "total",
      onDownload: () => downloadCsv("ventas-totales.csv", orderCsvRows(overview.orders)),
    },
  ];

  return (
    <div id="finance-logistics-channels-target">
      <ChannelTable
        title="Canales de venta"
        subtitle="Qué parte del valor de pedidos está explicada por Fluxi."
        rows={rows}
        currency={summary.currency}
      />
    </div>
  );
}

function StatusPill({ status, label }: { status: string; label: string }) {
  const meta = statusMeta[status] ?? statusMeta.pending;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${meta.bg} ${meta.color}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.dot }} />
      {label}
    </span>
  );
}

export default LogisticsTab;
